import { addDays, endOfMonth, startOfMonth } from 'date-fns';

import {
  ComparisonOperator,
  type ComparisonQueryNode,
  type LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { formatDate } from '@blaze-shared/shared-util';

export interface CalendarRange {
  since: Date;
  until: Date;
}

export function getCalendarRangeQuery(
  startColumnId: string | undefined,
  endColumnId: string | undefined,
  range?: CalendarRange,
): LogicalQueryNode | undefined {
  if (!startColumnId || !endColumnId) {
    return undefined;
  }
  if (!range) {
    range = getDefaultCalendarRange();
  }
  const query: LogicalQueryNode = {
    nodeType: QueryNodeType.Logical,
    logicalType: LogicalType.Or,
    children: [
      {
        // since <= [start && end] <= until
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          getQueryChild(startColumnId, ComparisonOperator.Gte, range.since),
          getQueryChild(endColumnId, ComparisonOperator.Lte, range.until),
        ],
      },
      {
        // [start < since <= end] ... until
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          getQueryChild(startColumnId, ComparisonOperator.Lt, range.since),
          getQueryChild(endColumnId, ComparisonOperator.Gte, range.since),
        ],
      },
      {
        // since ... [start <= until < end]
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          getQueryChild(startColumnId, ComparisonOperator.Lte, range.until),
          getQueryChild(endColumnId, ComparisonOperator.Gt, range.until),
        ],
      },
      {
        // [start < since && end > until]
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          getQueryChild(startColumnId, ComparisonOperator.Lt, range.since),
          getQueryChild(endColumnId, ComparisonOperator.Gt, range.until),
        ],
      },
    ],
  };

  return query;
}

export function getDefaultCalendarRange(): CalendarRange {
  const today = new Date();
  return {
    since: addDays(startOfMonth(today), -7),
    until: addDays(endOfMonth(today), 7),
  };
}

function getQueryChild(
  column: string,
  operator: ComparisonOperator,
  date: Date,
): ComparisonQueryNode {
  const value = date.toISOString();
  return {
    nodeType: QueryNodeType.Comparison,
    column,
    operator,
    value,
    isOptional: false,
    isFormulaValue: false,
    displayInfos: [
      {
        display: formatDate(true)(date),
        formulaName: '',
        isAutoCompleted: true,
        value,
      },
    ],
  };
}
